/**
 * Fetch-based implementation of OneAdvancedApi.
 */

import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import type { OneAdvancedApi } from './OneAdvancedApi';
import type {
  ContentType,
  CreateCategoryInput,
  CreateSectionInput,
  LayoutCategory,
  LayoutItem,
  LayoutSection,
  ReorderItem,
  UpdateCategoryInput,
  UpdateSectionInput,
} from '../modules/layouts/types';
import type {
  Banner,
  CreateBannerInput,
  UpdateBannerInput,
} from '../modules/banners/types';
import type { ToolchainApproval } from '../modules/pending-approvals/types';
import { toSlug } from '../modules/faq/utils';
import type {
  FaqSection,
  FaqCategory,
  FaqEntry,
  FeedbackSummary,
  FeedbackVoters,
  ReorderFaqItem,
  CreateFaqSectionInput,
  UpdateFaqSectionInput,
  CreateFaqCategoryInput,
  UpdateFaqCategoryInput,
  CreateFaqEntryInput,
  UpdateFaqEntryInput,
} from '../modules/faq/types';

interface BannerRow {
  id: string;
  title: string;
  message: string;
  severity: string;
  status: string;
  dismissable: boolean;
  expires_at: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

function toBanner(row: BannerRow): Banner {
  return {
    id: row.id,
    title: row.title,
    message: row.message,
    severity: row.severity as Banner['severity'],
    status: row.status as Banner['status'],
    dismissable: row.dismissable,
    expiresAt: row.expires_at ?? undefined,
    sortOrder: row.sort_order,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// ---------------------------------------------------------------------------
// Row types returned from the backend (snake_case)
// ---------------------------------------------------------------------------

interface SectionRow {
  id: string;
  name: string;
  content_type: ContentType;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface CategoryRow {
  id: string;
  section_id: string | null;
  section_name: string | null;
  name: string;
  description: string | null;
  icon: string | null;
  accent_color: string | null;
  content_type: ContentType;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface ItemRow {
  id: string;
  category_id: string;
  entity_ref: string;
  sort_order: number;
  created_at: string;
}

function toSection(row: SectionRow): LayoutSection {
  return {
    id: row.id,
    name: row.name,
    contentType: row.content_type,
    sortOrder: row.sort_order,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function toCategory(row: CategoryRow): LayoutCategory {
  return {
    id: row.id,
    sectionId: row.section_id ?? undefined,
    section: row.section_name ?? undefined,
    name: row.name,
    description: row.description ?? undefined,
    icon: row.icon ?? undefined,
    accentColor: row.accent_color ?? undefined,
    contentType: row.content_type,
    sortOrder: row.sort_order,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function toItem(row: ItemRow): LayoutItem {
  return {
    id: row.id,
    categoryId: row.category_id,
    entityRef: row.entity_ref,
    sortOrder: row.sort_order,
    createdAt: row.created_at,
  };
}

// ---------------------------------------------------------------------------
// FAQ row types returned from the backend (snake_case)
// ---------------------------------------------------------------------------

interface FaqSectionRow {
  id: string;
  name: string;
  color: string;
  description: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface FaqCategoryRow {
  id: string;
  section_id: string;
  section_name: string;
  name: string;
  sort_order: number;
  entry_count: number | string;
  created_at: string;
  updated_at: string;
}

interface FaqEntryRow {
  id: string;
  slug: string;
  category_id: string;
  category_name: string;
  category_sort_order: number;
  section_name: string;
  section_color: string;
  section_sort_order: number;
  question: string;
  answer: string;
  keywords: string[];
  links: FaqEntry['links'];
  status: FaqEntry['status'];
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

function toFaqSection(row: FaqSectionRow): FaqSection {
  return {
    id: row.id,
    name: row.name,
    slug: toSlug(row.name),
    color: row.color,
    description: row.description,
    sortOrder: row.sort_order,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function toFaqCategory(row: FaqCategoryRow): FaqCategory {
  return {
    id: row.id,
    sectionId: row.section_id,
    sectionSlug: toSlug(row.section_name),
    sectionName: row.section_name,
    name: row.name,
    sortOrder: row.sort_order,
    entryCount: Number(row.entry_count ?? 0),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function toFaqEntry(row: FaqEntryRow): FaqEntry {
  return {
    id: row.id,
    slug: row.slug ?? row.id,
    categoryId: row.category_id,
    categoryName: row.category_name,
    categorySortOrder: row.category_sort_order,
    sectionSlug: toSlug(row.section_name),
    sectionName: row.section_name,
    sectionColor: row.section_color,
    sectionSortOrder: row.section_sort_order,
    question: row.question,
    answer: row.answer,
    keywords: row.keywords,
    links: row.links,
    status: row.status,
    createdBy: row.created_by,
    updatedBy: row.updated_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// ---------------------------------------------------------------------------
// Client implementation
// ---------------------------------------------------------------------------

export class OneAdvancedClient implements OneAdvancedApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async baseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('oneadvanced');
  }

  private async request<T>(
    path: string,
    options?: RequestInit,
  ): Promise<T> {
    const base = await this.baseUrl();
    const resp = await this.fetchApi.fetch(`${base}${path}`, {
      headers: { 'Content-Type': 'application/json' },
      ...options,
    });
    if (!resp.ok) {
      const body = await resp.text();
      let message = `Request failed with status ${resp.status}`;
      try {
        const json = JSON.parse(body);
        // Backstage standard: { error: { message: "..." } }
        // Simple format:      { error: "..." }
        if (json?.error?.message) message = json.error.message;
        else if (typeof json?.error === 'string') message = json.error;
        else if (typeof json?.message === 'string') message = json.message;
      } catch {
        // body was plain text — use status fallback above
        if (body) message = body;
      }
      throw new Error(message);
    }
    if (resp.status === 204) {
      return undefined as unknown as T;
    }
    return resp.json() as Promise<T>;
  }

  readonly layouts = {
    // ── Sections ─────────────────────────────────────────────────────────────

    getSections: async (contentType: ContentType): Promise<LayoutSection[]> => {
      const rows = await this.request<SectionRow[]>(
        `/layouts/${contentType}/sections`,
      );
      return rows.map(toSection);
    },

    createSection: async (
      contentType: ContentType,
      data: CreateSectionInput,
    ): Promise<LayoutSection> => {
      const row = await this.request<SectionRow>(
        `/layouts/${contentType}/sections`,
        { method: 'POST', body: JSON.stringify(data) },
      );
      return toSection(row);
    },

    updateSection: async (
      contentType: ContentType,
      id: string,
      data: UpdateSectionInput,
    ): Promise<LayoutSection> => {
      const row = await this.request<SectionRow>(
        `/layouts/${contentType}/sections/${id}`,
        { method: 'PUT', body: JSON.stringify(data) },
      );
      return toSection(row);
    },

    deleteSection: async (contentType: ContentType, id: string): Promise<void> => {
      await this.request<void>(`/layouts/${contentType}/sections/${id}`, {
        method: 'DELETE',
      });
    },

    reorderSections: async (
      contentType: ContentType,
      orders: ReorderItem[],
    ): Promise<void> => {
      await this.request<void>(`/layouts/${contentType}/sections/reorder`, {
        method: 'PUT',
        body: JSON.stringify(orders),
      });
    },

    // ── Categories ────────────────────────────────────────────────────────────

    getCategories: async (contentType: ContentType): Promise<LayoutCategory[]> => {
      const rows = await this.request<CategoryRow[]>(
        `/layouts/${contentType}/categories`,
      );
      return rows.map(toCategory);
    },

    createCategory: async (
      contentType: ContentType,
      data: CreateCategoryInput,
    ): Promise<LayoutCategory> => {
      const row = await this.request<CategoryRow>(
        `/layouts/${contentType}/categories`,
        { method: 'POST', body: JSON.stringify(data) },
      );
      return toCategory(row);
    },

    updateCategory: async (
      contentType: ContentType,
      id: string,
      data: UpdateCategoryInput,
    ): Promise<LayoutCategory> => {
      const row = await this.request<CategoryRow>(
        `/layouts/${contentType}/categories/${id}`,
        { method: 'PUT', body: JSON.stringify(data) },
      );
      return toCategory(row);
    },

    deleteCategory: async (contentType: ContentType, id: string): Promise<void> => {
      await this.request<void>(`/layouts/${contentType}/categories/${id}`, {
        method: 'DELETE',
      });
    },

    reorderCategories: async (
      contentType: ContentType,
      orders: ReorderItem[],
    ): Promise<void> => {
      await this.request<void>(`/layouts/${contentType}/categories/reorder`, {
        method: 'PUT',
        body: JSON.stringify(orders),
      });
    },

    // ── Items ─────────────────────────────────────────────────────────────────

    getCategoryItems: async (
      contentType: ContentType,
      categoryId: string,
    ): Promise<LayoutItem[]> => {
      const rows = await this.request<ItemRow[]>(
        `/layouts/${contentType}/categories/${categoryId}/items`,
      );
      return rows.map(toItem);
    },

    addItem: async (
      contentType: ContentType,
      categoryId: string,
      entityRef: string,
    ): Promise<LayoutItem> => {
      const row = await this.request<ItemRow>(
        `/layouts/${contentType}/categories/${categoryId}/items`,
        { method: 'POST', body: JSON.stringify({ entity_ref: entityRef }) },
      );
      return toItem(row);
    },

    removeItem: async (
      contentType: ContentType,
      categoryId: string,
      itemId: string,
    ): Promise<void> => {
      await this.request<void>(
        `/layouts/${contentType}/categories/${categoryId}/items/${itemId}`,
        { method: 'DELETE' },
      );
    },

    reorderItems: async (
      contentType: ContentType,
      categoryId: string,
      orders: ReorderItem[],
    ): Promise<void> => {
      await this.request<void>(
        `/layouts/${contentType}/categories/${categoryId}/items/reorder`,
        { method: 'PUT', body: JSON.stringify(orders) },
      );
    },
  };

  readonly banners = {
    getActiveBanners: async (): Promise<Banner[]> => {
      const rows = await this.request<BannerRow[]>('/banners');
      return rows.map(toBanner);
    },

    getAllBanners: async (): Promise<Banner[]> => {
      const rows = await this.request<BannerRow[]>('/banners/admin');
      return rows.map(toBanner);
    },

    createBanner: async (data: CreateBannerInput): Promise<Banner> => {
      const row = await this.request<BannerRow>('/banners', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return toBanner(row);
    },

    updateBanner: async (id: string, data: UpdateBannerInput): Promise<Banner> => {
      const row = await this.request<BannerRow>(`/banners/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return toBanner(row);
    },

    deleteBanner: async (id: string): Promise<void> => {
      await this.request<void>(`/banners/${id}`, { method: 'DELETE' });
    },

    enableBanner: async (id: string): Promise<Banner> => {
      const row = await this.request<BannerRow>(`/banners/${id}/enable`, {
        method: 'POST',
      });
      return toBanner(row);
    },

    disableBanner: async (id: string): Promise<Banner> => {
      const row = await this.request<BannerRow>(`/banners/${id}/disable`, {
        method: 'POST',
      });
      return toBanner(row);
    },

  };

  readonly toolchainApprovals = {
    getPendingApprovals: async (): Promise<{ data: ToolchainApproval[] }> => {
      return this.request('/pending-approvals/pending');
    },
    getExecutionStatus: async (taskIds: string[]): Promise<{ data: Record<string, any> }> => {
      return this.request('/pending-approvals/execution-status', {
        method: 'POST',
        body: JSON.stringify({ taskIds }),
      });
    },
    getTaskOutputs: async (taskId: string): Promise<{ data: Record<string, string> | null }> => {
      return this.request(`/pending-approvals/execution-status/${encodeURIComponent(taskId)}/outputs`);
    },
  };

  readonly faq = {
    // ── Owners ───────────────────────────────────────────────────────────────

    getSections: async (): Promise<FaqSection[]> => {
      const rows = await this.request<FaqSectionRow[]>('/faqs/sections');
      return rows.map(toFaqSection);
    },

    createSection: async (data: CreateFaqSectionInput): Promise<FaqSection> => {
      const row = await this.request<FaqSectionRow>('/faqs/sections', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return toFaqSection(row);
    },

    updateSection: async (id: string, data: UpdateFaqSectionInput): Promise<FaqSection> => {
      const row = await this.request<FaqSectionRow>(`/faqs/sections/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return toFaqSection(row);
    },

    deleteSection: async (id: string): Promise<void> => {
      await this.request<void>(`/faqs/sections/${id}`, { method: 'DELETE' });
    },

    reorderSections: async (items: ReorderFaqItem[]): Promise<void> => {
      await this.request<void>('/faqs/sections/reorder', {
        method: 'PUT',
        body: JSON.stringify(items.map(i => ({ id: i.id, sort_order: i.sortOrder }))),
      });
    },

    // ── Categories ────────────────────────────────────────────────────────────

    getCategories: async (sectionId: string): Promise<FaqCategory[]> => {
      const rows = await this.request<FaqCategoryRow[]>(`/faqs/sections/${sectionId}/categories`);
      return rows.map(toFaqCategory);
    },

    createCategory: async (sectionId: string, data: CreateFaqCategoryInput): Promise<FaqCategory> => {
      const row = await this.request<FaqCategoryRow>(`/faqs/sections/${sectionId}/categories`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return toFaqCategory(row);
    },

    updateCategory: async (sectionId: string, id: string, data: UpdateFaqCategoryInput): Promise<FaqCategory> => {
      const row = await this.request<FaqCategoryRow>(`/faqs/sections/${sectionId}/categories/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return toFaqCategory(row);
    },

    deleteCategory: async (sectionId: string, id: string): Promise<void> => {
      await this.request<void>(`/faqs/sections/${sectionId}/categories/${id}`, { method: 'DELETE' });
    },

    reorderCategories: async (sectionId: string, items: ReorderFaqItem[]): Promise<void> => {
      await this.request<void>(`/faqs/sections/${sectionId}/categories/reorder`, {
        method: 'PUT',
        body: JSON.stringify(items.map(i => ({ id: i.id, sort_order: i.sortOrder }))),
      });
    },

    // ── Entries ───────────────────────────────────────────────────────────────

    getPublishedEntries: async (): Promise<FaqEntry[]> => {
      const rows = await this.request<FaqEntryRow[]>('/faqs/entries');
      return rows.map(toFaqEntry);
    },

    searchEntries: async (q: string): Promise<FaqEntry[]> => {
      const rows = await this.request<FaqEntryRow[]>(`/faqs/entries/search?q=${encodeURIComponent(q)}`);
      return rows.map(toFaqEntry);
    },

    getAllEntries: async (): Promise<FaqEntry[]> => {
      const rows = await this.request<FaqEntryRow[]>('/faqs/entries/admin');
      return rows.map(toFaqEntry);
    },

    createEntry: async (categoryId: string, data: CreateFaqEntryInput): Promise<FaqEntry> => {
      const row = await this.request<FaqEntryRow>(`/faqs/categories/${categoryId}/entries`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return toFaqEntry(row);
    },

    updateEntry: async (id: string, data: UpdateFaqEntryInput): Promise<FaqEntry> => {
      const row = await this.request<FaqEntryRow>(`/faqs/entries/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return toFaqEntry(row);
    },

    deleteEntry: async (id: string): Promise<void> => {
      await this.request<void>(`/faqs/entries/${id}`, { method: 'DELETE' });
    },

    getFeedback: async (entryId: string): Promise<FeedbackSummary> => {
      return this.request<FeedbackSummary>(`/faqs/entries/${encodeURIComponent(entryId)}/feedback`);
    },

    submitFeedback: async (entryId: string, helpful: boolean): Promise<FeedbackSummary> => {
      return this.request<FeedbackSummary>(`/faqs/entries/${encodeURIComponent(entryId)}/feedback`, {
        method: 'POST',
        body: JSON.stringify({ helpful }),
      });
    },

    getFeedbackVoters: async (entryId: string): Promise<FeedbackVoters> => {
      return this.request<FeedbackVoters>(`/faqs/entries/${encodeURIComponent(entryId)}/feedback/voters`);
    },

    getEntriesBySection: async (sectionId: string): Promise<FaqEntry[]> => {
      const rows = await this.request<FaqEntryRow[]>(`/faqs/sections/${encodeURIComponent(sectionId)}/entries`);
      return rows.map(toFaqEntry);
    },

    getEntriesByCategory: async (categoryId: string): Promise<FaqEntry[]> => {
      const rows = await this.request<FaqEntryRow[]>(`/faqs/categories/${encodeURIComponent(categoryId)}/entries`);
      return rows.map(toFaqEntry);
    },

    getAdminEntriesByCategory: async (categoryId: string): Promise<FaqEntry[]> => {
      const rows = await this.request<FaqEntryRow[]>(`/faqs/categories/${encodeURIComponent(categoryId)}/entries/admin`);
      return rows.map(toFaqEntry);
    },

    getRecentEntries: async (limit = 8): Promise<FaqEntry[]> => {
      const rows = await this.request<FaqEntryRow[]>(`/faqs/entries/recent?limit=${limit}`);
      return rows.map(toFaqEntry);
    },

    getEntryBySlug: async (slug: string): Promise<FaqEntry> => {
      const row = await this.request<FaqEntryRow>(`/faqs/entries/by-slug/${encodeURIComponent(slug)}`);
      return toFaqEntry(row);
    },
  };
}
